<template>
  <div id="app">
    <!-- 动态背景 -->
    <div v-if="isDynamicBackgroundActive" class="dynamic-background"></div>
    
    <!-- 主题切换器 -->
    <div class="theme-switcher">
      <div class="theme-section">
        <button
          class="theme-btn"
          :class="{ active: currentTheme === 'default' }"
          @click="switchTheme('default')"
          :title="t('common.theme.default').value"
        >
          🌞
        </button>
        <button
          class="theme-btn"
          :class="{ active: currentTheme === 'dark' }"
          @click="switchTheme('dark')"
          :title="t('common.theme.dark').value"
        >
          🌙
        </button>
        <button
          class="theme-btn"
          :class="{ active: currentTheme === 'glass' }"
          @click="switchTheme('glass')"
          :title="t('common.theme.glass').value"
        >
          🪟
        </button>
      </div>
    </div>

    <!-- 语言切换器（右下角） -->
    <div class="language-switcher">
      <div class="language-section">
        <button
          class="lang-btn"
          :class="{ active: language === 'zh-CN' }"
          @click="handleSwitchLanguage('zh-CN')"
          title="简体中文"
        >
          中文
        </button>
        <button
          class="lang-btn"
          :class="{ active: language === 'en-US' }"
          @click="handleSwitchLanguage('en-US')"
          title="English"
        >
          EN
        </button>
        <button
          class="lang-btn"
          :class="{ active: language === 'de-DE' }"
          @click="handleSwitchLanguage('de-DE')"
          title="Deutsch"
        >
          DE
        </button>
        <button
          class="lang-btn"
          :class="{ active: language === 'ja-JP' }"
          @click="handleSwitchLanguage('ja-JP')"
          title="日本語"
        >
          JA
        </button>
        <!-- 预留更多语言按钮的位置 -->
        <!-- 未来可以动态添加 -->
      </div>
    </div>
    
    <main class="app-main">
      <div class="container">
      
        <!-- Tab导航菜单 -->
        <nav class="tab-nav">
          <div class="tab-scroll-container">
            <div class="tab-list">
              <div class="app-title-container" @click="handleLogoClick">
                <h1 class="app-title">
                  <span class="logo-icon">🪸</span>
                  {{ t('common.appTitle').value }}
                </h1>
                <!-- 点击计数显示 -->
                <div class="click-counter" v-if="clickCount > 0">
                  <span class="counter-badge">{{ clickCount }}</span>
                </div>
                <!-- +1 动画 -->
                <div v-if="showPlusOne" class="plus-one-animation">+1</div>
              </div>
              <button
                v-for="tab in tabs"
                :key="tab.id"
                :class="['tab-button', { active: activeTab === tab.id }]"
                @click="activeTab = tab.id"
              >
                <span class="tab-icon">{{ tab.icon }}</span>
                <span class="tab-label">{{ tab.label }}</span>
              </button>
            </div>
          </div>
        </nav>

        <!-- 内容区域 -->
        <div class="content-area">
          <!-- AI水质顾问 (默认显示) -->
          <div v-show="activeTab === 'ai-advisor'" class="tab-content">
            <AIAdvisor />
          </div>

          <!-- PAR分析器 -->
          <div v-show="activeTab === 'par-analyzer'" class="tab-content">
            <PARAnalyzer />
          </div>

          <!-- 水体与换水管理 -->
          <div v-show="activeTab === 'water-management'" class="tab-content">
            <WaterManagementCalculator />
          </div>

          <!-- 元素计算器 -->
          <div v-show="activeTab === 'elements'" class="tab-content">
            <ElementsCalculator />
          </div>

          <!-- 营养盐计算器 -->
          <div v-show="activeTab === 'nutrients'" class="tab-content">
            <NutrientsCalculator />
          </div>

          <!-- 喂食计算器 -->
          <div v-show="activeTab === 'feeding'" class="tab-content">
            <FeedingCalculator />
          </div>

          <!-- 紧急工具 -->

          <!-- 缸体日志 -->
          <div v-show="activeTab === 'tank-log'" class="tab-content">
            <TankLog />
          </div>

          <!-- 检疫追踪 -->
          <div v-show="activeTab === 'quarantine'" class="tab-content">
            <QuarantineTracker />
          </div>
        </div>
      </div>
    </main>

    <!-- 移动端底部导航 -->
    <nav v-if="isMobile" class="mobile-bottom-nav">
      <div class="nav-container">
        <button
          v-for="tab in mobileTabs"
          :key="tab.id"
          :class="['nav-button', { active: activeTab === tab.id }]"
          @click="activeTab = tab.id"
        >
          <span class="nav-icon">{{ tab.icon }}</span>
          <span class="nav-label">{{ tab.label }}</span>
        </button>
      </div>
    </nav>

    <footer class="app-footer">
      <div class="footer-content">
        <p>{{ t('common.footer').value }}</p>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AIAdvisor from './components/AIAdvisor.vue'
import WaterManagementCalculator from './components/WaterManagementCalculator.vue'
import ElementsCalculator from './components/ElementsCalculator.vue'
import NutrientsCalculator from './components/NutrientsCalculator.vue'
import FeedingCalculator from './components/FeedingCalculator.vue'
import PARAnalyzer from './components/PARAnalyzer.vue'
import TankLog from './components/TankLog.vue'
import QuarantineTracker from './components/QuarantineTracker.vue'
import { useAppStore } from './stores/app'
import { useI18n } from './composables/useI18n'
import { useHead } from './composables/useHead'

// 路由
const route = useRoute()
const router = useRouter()

// Tab状态管理
const activeTab = ref('ai-advisor')

// 移动端检测
const isMobile = ref(false)

// 应用状态管理
const appStore = useAppStore()
const currentTheme = computed(() => appStore.userPreferences.theme)
const { t, language, switchLanguage } = useI18n()

// SEO Head管理
useHead()

// 路由路径到标签ID的映射
const routeToTabMap: Record<string, string> = {
  '/ai-advisor': 'ai-advisor',
  '/par-analyzer': 'par-analyzer',
  '/water-change': 'water-management',
  '/elements': 'elements',
  '/nutrients': 'nutrients',
  '/feeding': 'feeding',
  '/tank-log': 'tank-log',
  '/quarantine': 'quarantine'
}

// 根据路由更新activeTab
watch(() => route.path, (path) => {
  const tabId = routeToTabMap[path]
  if (tabId && activeTab.value !== tabId) {
    activeTab.value = tabId
  }
}, { immediate: true })

// 当activeTab变化时更新路由（可选）
watch(activeTab, (newTab) => {
  const path = Object.keys(routeToTabMap).find(key => routeToTabMap[key] === newTab)
  if (path && route.path !== path) {
    router.push(path)
  }
})

// 彩蛋相关状态
const clickCount = ref(0)
const showPlusOne = ref(false)
const isDynamicBackgroundActive = ref(false)
let clickTimeout: number | null = null
let resetTimeout: number | null = null

// Tab配置 - 每个功能独立一个tab
const tabs = computed(() => [
  { id: 'ai-advisor', label: t('common.tabs.aiAdvisor').value, icon: '🤖' },
  { id: 'par-analyzer', label: t('common.tabs.parAnalyzer').value, icon: '💡' },
  { id: 'water-management', label: t('common.tabs.waterManagement').value, icon: '💧' },
  { id: 'elements', label: t('common.tabs.elements').value, icon: '🧪' },
  { id: 'nutrients', label: t('common.tabs.nutrients').value, icon: '📊' },
  { id: 'feeding', label: t('common.tabs.feeding').value, icon: '🍽️' },
  { id: 'quarantine', label: t('common.tabs.quarantine').value, icon: '🛡️' },
  { id: 'tank-log', label: t('common.tabs.tankLog').value, icon: '📝' }
])

// 移动端底部导航配置
const mobileTabs = computed(() => [
  { id: 'ai-advisor', label: t('common.tabs.aiAdvisor').value, icon: '🤖' },
  { id: 'par-analyzer', label: t('common.tabs.parAnalyzer').value, icon: '💡' },
  { id: 'water-management', label: t('common.tabs.waterManagement').value, icon: '💧' },
  { id: 'elements', label: t('common.tabs.elements').value, icon: '🧪' },
  { id: 'nutrients', label: t('common.tabs.nutrients').value, icon: '📊' },
  { id: 'feeding', label: t('common.tabs.feeding').value, icon: '🍽️' },
  { id: 'quarantine', label: t('common.tabs.quarantine').value, icon: '🛡️' },
  { id: 'tank-log', label: t('common.tabs.tankLog').value, icon: '📝' }
])

// 处理logo点击
const handleLogoClick = (event: Event) => {
  // 阻止默认行为和冒泡，避免浏览器右键菜单
  event.preventDefault()
  event.stopPropagation()
  
  // 增加点击计数
  clickCount.value++
  
  // 显示+1动画
  showPlusOne.value = true
  setTimeout(() => {
    showPlusOne.value = false
  }, 1000)
  
  // 检查彩蛋触发条件
  checkEasterEggs()
  
  // 重置超时逻辑（30秒内无点击则重置计数）
  if (resetTimeout) {
    clearTimeout(resetTimeout)
  }
  resetTimeout = setTimeout(() => {
    clickCount.value = 0
    isDynamicBackgroundActive.value = false
  }, 30000)
}

// 检查彩蛋触发
const checkEasterEggs = () => {
  // 10次点击彩蛋
  if (clickCount.value === 10) {
    triggerTenClickEasterEgg()
  }
  
  // 100次点击彩蛋
  if (clickCount.value === 100) {
    triggerHundredClickEasterEgg()
  }
}

// 10次点击彩蛋效果
const triggerTenClickEasterEgg = () => {
  // 添加一些有趣的动画效果
  const logoIcon = document.querySelector('.logo-icon')
  if (logoIcon) {
    logoIcon.classList.add('ten-clicks-animation')
    setTimeout(() => {
      logoIcon.classList.remove('ten-clicks-animation')
    }, 2000)
  }
  
  // 添加庆祝效果
  const titleContainer = document.querySelector('.app-title-container')
  if (titleContainer) {
    titleContainer.classList.add('celebrate')
    setTimeout(() => {
      titleContainer.classList.remove('celebrate')
    }, 1500)
  }
}

// 100次点击彩蛋效果 - 动态背景
const triggerHundredClickEasterEgg = () => {
  isDynamicBackgroundActive.value = true
}

// 检测移动端
const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768
}

// 切换主题
const switchTheme = (theme: string) => {
  appStore.switchTheme(theme)
}

// 切换语言
const handleSwitchLanguage = async (lang: string) => {
  await switchLanguage(lang)
}

// 组件挂载时检测移动端
onMounted(async () => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
  // 初始化应用状态
  await appStore.initialize()

  // 检查URL中的语言参数
  const langParam = route.query.lang
  if (langParam && typeof langParam === 'string') {
    // 验证语言是否支持
    const supportedLangs = ['zh-CN', 'en-US', 'de-DE', 'ja-JP']
    if (supportedLangs.includes(langParam)) {
      await switchLanguage(langParam)
    }
  }
})

// 组件卸载时清理定时器和事件监听器
onUnmounted(() => {
  if (clickTimeout) clearTimeout(clickTimeout)
  if (resetTimeout) clearTimeout(resetTimeout)
  window.removeEventListener('resize', checkMobile)
})
</script>

<style>
/* 全局样式重置 */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  line-height: 1.6;
  color: var(--color-text-primary);
  background: var(--color-bg-primary);
  min-height: 100vh;
  position: relative;
  transition: all var(--transition-normal);
}

/* 动态背景效果 */
.dynamic-background {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  pointer-events: none;
}

.dynamic-background::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    45deg,
    #667eea,
    #764ba2,
    #f093fb,
    #f5576c,
    #4facfe,
    #00f2fe
  );
  background-size: 400% 400%;
  animation: gradientShift 15s ease infinite;
  opacity: 0.1;
}

.dynamic-background::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3) 0%, transparent 50%),
              radial-gradient(circle at 40% 40%, rgba(120, 219, 255, 0.3) 0%, transparent 50%);
  animation: float 20s ease-in-out infinite;
}

@keyframes gradientShift {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

@keyframes float {
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(180deg); }
}

/* 点击计数显示 */
.click-counter {
  position: absolute;
  top: -6px;
  left: 38px;
  z-index: 1000;
}

.counter-badge {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  padding: 2px 6px;
  border-radius: 50%; /* 改为圆形 */
  font-size: 0.7rem;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
  animation: pulse 2s infinite;
  min-width: 20px;
  height: 20px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

/* Logo图标样式 */
.logo-icon {
  display: inline-block;
  margin-right: 8px;
  transition: all 0.3s ease;
  cursor: pointer;
  color: inherit; /* 移除-webkit-text-fill-color的影响 */
  -webkit-text-fill-color: initial;
}

.app-title-container {
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;
  /* 完全阻止文字选择和上下文菜单 */
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

/* 移除鼠标悬停旋转效果 */
.app-title-container:hover .logo-icon {
  transform: scale(1.1); /* 只保留缩放效果，移除旋转 */
}

/* 阻止整个应用区域的文字选择 */
.app-title,
.logo-icon {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

/* +1 动画 */
.plus-one-animation {
  position: absolute;
  top: -40px; /* 提高位置避免遮挡 */
  left: 50%;
  transform: translateX(-50%);
  color: #667eea;
  font-weight: bold;
  font-size: 1.4rem;
  animation: floatUp 1.5s ease-out forwards;
  pointer-events: none;
  z-index: 1000;
  text-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

@keyframes floatUp {
  0% {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
  100% {
    opacity: 0;
    transform: translateX(-50%) translateY(-30px);
  }
}

/* 10次点击动画 */
.ten-clicks-animation {
  animation: celebrate 2s ease-in-out;
}

@keyframes celebrate {
  0% {
    transform: scale(1) rotate(0deg);
    filter: hue-rotate(0deg);
  }
  25% {
    transform: scale(1.8) rotate(90deg);
    filter: hue-rotate(90deg);
  }
  50% {
    transform: scale(1.3) rotate(180deg);
    filter: hue-rotate(180deg);
  }
  75% {
    transform: scale(1.8) rotate(270deg);
    filter: hue-rotate(270deg);
  }
  100% {
    transform: scale(1) rotate(360deg);
    filter: hue-rotate(360deg);
  }
}

/* 庆祝效果 */
.app-title-container.celebrate {
  animation: celebrateContainer 1.5s ease-in-out;
}

@keyframes celebrateContainer {
  0% {
    transform: scale(1);
    background: transparent;
  }
  25% {
    transform: scale(1.1);
    background: rgba(102, 126, 234, 0.1);
  }
  50% {
    transform: scale(1.05);
    background: rgba(118, 75, 162, 0.1);
  }
  75% {
    transform: scale(1.1);
    background: rgba(102, 126, 234, 0.1);
  }
  100% {
    transform: scale(1);
    background: transparent;
  }
}

/* 点击反馈效果 */
.app-title-container:active {
  transform: scale(0.95);
}

#app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* 应用标题容器 */
.app-title-container {
  display: flex;
  align-items: center;
  margin-right: 20px;
  padding: 0 15px;
  border-right: 1px solid rgba(102, 126, 234, 0.2);
}

.app-title {
  font-size: 1.3rem;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;
  white-space: nowrap;
}

/* Tab导航菜单 */
.tab-nav {
  margin-bottom: 2rem;
  background: var(--color-bg-secondary);
  border-radius: var(--radius-lg);
  padding: var(--space-sm);
  box-shadow: var(--shadow-lg);
  backdrop-filter: var(--backdrop-blur);
  position: relative;
  border: 1px solid var(--color-border-primary);
}

.tab-scroll-container {
  overflow-x: auto;
  scrollbar-width: thin; /* Firefox */
  scrollbar-color: rgba(102, 126, 234, 0.3) transparent;
  -ms-overflow-style: -ms-autohiding-scrollbar; /* IE and Edge */
  padding-bottom: 4px; /* 为滚动条留空间 */
}

.tab-scroll-container::-webkit-scrollbar {
  height: 6px;
}

.tab-scroll-container::-webkit-scrollbar-track {
  background: rgba(102, 126, 234, 0.1);
  border-radius: 3px;
}

.tab-scroll-container::-webkit-scrollbar-thumb {
  background: rgba(102, 126, 234, 0.3);
  border-radius: 3px;
}

.tab-scroll-container::-webkit-scrollbar-thumb:hover {
  background: rgba(102, 126, 234, 0.5);
}

.tab-list {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-start;
  align-items: center;
  min-width: max-content;
  padding: 0 0.25rem;
}

.tab-button {
  display: flex;
  align-items: center;
  gap: 0.2rem;
  padding: var(--space-md) var(--space-sm);
  border: none;
  background: transparent;
  border-radius: var(--radius-md);
  font-size: var(--text-base);
  font-weight: var(--font-medium);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all var(--transition-normal);
  position: relative;
  overflow: hidden;
  white-space: nowrap;
  flex-shrink: 0;
  min-width: fit-content;
}

.tab-button:hover {
  background: rgba(102, 126, 234, 0.1);
  color: var(--color-primary);
  transform: translateY(-1px);
}

.tab-button.active {
  background: var(--gradient-primary);
  color: var(--color-text-inverse);
  box-shadow: var(--shadow-md);
}

.tab-icon {
  font-size: 1.1rem;
}

.tab-label {
  font-weight: 500;
}

/* 主内容区域 */
.app-main {
  flex: 1;
  padding: var(--space-xl) 0;
}

.container {
  max-width: 1680px;
  margin: 0 auto;
  padding: 0 var(--space-lg);
}

.content-area {
  min-height: 60vh;
}

.tab-content {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 计算器网格布局 */
.calculator-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
  gap: 1.5rem;
  align-items: start;
}

.tools-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
  gap: 1.5rem;
  align-items: start;
}

/* 移动端底部导航 */
.mobile-bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 1px solid #e9ecef;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  padding: 8px 0;
}

.nav-container {
  display: flex;
  justify-content: space-around;
  align-items: center;
  max-width: 100%;
  margin: 0 auto;
}

.nav-button {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  border: none;
  background: transparent;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 8px;
  min-width: 60px;
  flex: 1;
}

.nav-button:hover {
  background: rgba(102, 126, 234, 0.1);
  color: #667eea;
}

.nav-button.active {
  color: #667eea;
  background: rgba(102, 126, 234, 0.1);
}

.nav-icon {
  font-size: 1.2rem;
  line-height: 1;
}

.nav-label {
  font-size: 0.7rem;
  font-weight: 500;
  line-height: 1;
}

/* 底部样式 */
.app-footer {
  background: var(--color-gray-900);
  color: var(--color-text-inverse);
  padding: var(--space-md) 0;
  margin-top: auto;
}

/* 移动端底部导航调整 */
@media (max-width: 768px) {
  .app-footer {
    padding-bottom: 70px; /* 为底部导航留出空间 */
  }
}

.footer-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  text-align: center;
}

.footer-content p {
  margin: 0;
  opacity: 0.8;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .nav-button {
    padding: 8px;
    min-width: initial;
  }

  /* 移动端隐藏顶部导航 */
  .tab-nav {
    display: none;
  }
  
  .app-title-container {
    margin-right: 15px;
    padding: 0 10px;
  }
  
  .app-title {
    font-size: 1.1rem;
  }
  
  .tab-list {
    gap: 0.25rem;
  }
  
  .tab-button {
    padding: 0.6rem 1rem;
    font-size: 0.9rem;
  }
  
  .tab-icon {
    font-size: 1rem;
  }
  
  .container {
    padding: 0 0.5rem;
  }
  
  .app-main {
    padding: 1rem 0;
    /* 为底部导航留出空间 */
    padding-bottom: 70px;
  }
  
  /* 移动端底部导航调整 */
  .mobile-bottom-nav {
    display: block;
  }
}

@media (min-width: 769px) {
  /* 桌面端隐藏底部导航 */
  .mobile-bottom-nav {
    display: none;
  }
}

@media (max-width: 640px) {
  .tab-button {
    padding: 0.5rem 0.8rem;
    font-size: 0.85rem;
  }
  
  .tab-icon {
    font-size: 0.9rem;
  }
  
  .tab-label {
    display: none; /* 在中等屏幕上只显示图标 */
  }
}

@media (max-width: 480px) {
  .app-title-container {
    margin-right: 10px;
    padding: 0 8px;
  }
  
  .app-title {
    font-size: 1rem;
  }
  
  .tab-nav {
    padding: 0.25rem;
  }
  
  .tab-button {
    padding: 0.4rem 0.6rem;
    font-size: 0.8rem;
  }
  
  .tab-icon {
    font-size: 0.85rem;
  }
}

@media (max-width: 360px) {
  .tab-button {
    padding: 0.35rem 0.5rem;
    font-size: 0.75rem;
  }
  
  .tab-icon {
    font-size: 0.8rem;
  }
}

/* 滚动提示 */
.tab-scroll-container {
  mask-image: linear-gradient(
    to right,
    transparent,
    black 20px,
    black calc(100% - 20px),
    transparent
  );
}

.tab-scroll-container.scroll-start {
  mask-image: linear-gradient(
    to right,
    black calc(100% - 20px),
    transparent
  );
}

.tab-scroll-container.scroll-end {
  mask-image: linear-gradient(
    to right,
    transparent,
    black 20px
  );
}

.tab-scroll-container.scroll-middle {
  mask-image: linear-gradient(
    to right,
    transparent,
    black 20px,
    black calc(100% - 20px),
    transparent
  );
}

/* 滚动条优化 */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: rgba(102, 126, 234, 0.6);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(102, 126, 234, 0.8);
}
/* 主题切换器 */
.theme-switcher {
  position: fixed;
  top: 20px;
  right: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 1000;
  background: var(--color-bg-secondary);
  padding: 12px;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  backdrop-filter: var(--backdrop-blur);
  border: 1px solid var(--color-border-primary);
}

.theme-section {
  display: flex;
  gap: 8px;
  justify-content: center;
}

/* 语言切换器（右下角） */
.language-switcher {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
  background: var(--color-bg-secondary);
  padding: 12px;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  backdrop-filter: var(--backdrop-blur);
  border: 1px solid var(--color-border-primary);
}

.language-section {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  max-width: 200px; /* 限制宽度，防止过多按钮溢出 */
}

.theme-btn {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--color-text-primary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  transition: all var(--transition-normal);
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--color-border-primary);
}

.theme-btn:hover {
  transform: scale(1.1);
  box-shadow: var(--shadow-md);
  background: rgba(102, 126, 234, 0.1);
}

.theme-btn.active {
  background: var(--gradient-primary);
  color: var(--color-text-inverse);
  transform: scale(1.15);
  box-shadow: var(--shadow-xl);
  border-color: transparent;
}

.lang-btn {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--color-text-primary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  font-weight: 600;
  transition: all var(--transition-normal);
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--color-border-primary);
}

.lang-btn:hover {
  transform: scale(1.1);
  box-shadow: var(--shadow-md);
  background: rgba(102, 126, 234, 0.1);
}

.lang-btn.active {
  background: var(--gradient-primary);
  color: var(--color-text-inverse);
  transform: scale(1.15);
  box-shadow: var(--shadow-xl);
  border-color: transparent;
}

/* 移动端主题切换器优化 */
@media (max-width: 768px) {
  .theme-switcher {
    top: 10px;
    right: 10px;
    gap: 6px;
    padding: 6px;
  }
  
  .theme-btn {
    width: 32px;
    height: 32px;
    font-size: 1rem;
  }
  
  .theme-btn:hover {
    transform: scale(1.05);
  }
  
  .theme-btn.active {
    transform: scale(1.1);
  }
  
  .lang-btn {
    width: 32px;
    height: 32px;
    font-size: 0.8rem;
  }
  
  .lang-btn:hover {
    transform: scale(1.05);
  }
  
  .lang-btn.active {
    transform: scale(1.1);
  }
  
  .language-switcher {
    bottom: 10px;
    right: 10px;
    padding: 6px;
    max-width: 180px;
  }
  
  .language-section {
    gap: 6px;
  }
}

@media (max-width: 480px) {
  .theme-switcher {
    top: 8px;
    right: 8px;
    gap: 4px;
    padding: 4px;
  }
  
  .theme-btn {
    width: 28px;
    height: 28px;
    font-size: 0.9rem;
  }
  
  .lang-btn {
    width: 28px;
    height: 28px;
    font-size: 0.7rem;
  }
  
  .language-switcher {
    bottom: 8px;
    right: 8px;
    padding: 4px;
    max-width: 160px;
  }
  
  .language-section {
    gap: 4px;
  }
}
</style>